import React, { FC, useEffect, useState, memo, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';

import Button from 'vibo-ui/Button';
import SongsList from './SongsList';
import Spinner from 'vibo-ui/Spinner';
import SongsBlockHeader from './SongsBlockHeader';
import TimelineSongsHeader from './TimelineSongsHeader';
import WithEventRole from 'components/user/WithEventRole';
import NoSongsFound from 'components/emptyStates/NoSongsFound';
import SectionBlock from 'components/sections/blocks/SectionBlock';
import SectionBlockFooter from 'components/sections/blocks/SectionBlock/SectionBlockFooter';
import { EventContext } from 'components/context/EventContext';
import { TimelineSongsContext } from './TimelineSongsContext';
import { SelectListProvider } from 'components/context/SelectListContext';

import { useSongs } from 'graphql/hooks/blocks';
import { usePrevious } from 'services/common/dataProcessingHelpers';
import { AllTimelineGqlOptions, useTimeline } from '../TimelineContext';
import { INITIAL_SONGS_LIMIT, LOAD_MORE_SONGS_COUNT } from 'services/constants';

import { isLoadingVar } from 'graphql/cache';

import { EventTimelineTabKey, EventUserType } from 'types/enums';
import { ControlProps } from 'components/sections/blocks/SectionBlock/interfaces';
import { SectionBlockBase } from 'components/sections/blocks/SectionBlocks/interfaces';

import useStyles from './style';

const TimelineSongsBlock: FC<SectionBlockBase> = ({ sections, section }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [controlRef, setControlRef] = useState<Nullable<ControlProps>>(null);

  const { isTemplate, isFavoriteSections, isLoading: isEventLoading } = useContext(EventContext);
  const { variables } = useTimeline<AllTimelineGqlOptions>();
  const isLoadingGlobal = useReactiveVar(isLoadingVar);

  const { id: eventId } = useParams<keyof EventPageRouteParams>();

  const prevSectionId = usePrevious(variables.sectionId);

  const songsOptions = useSongs({
    variables: {
      ...variables,
      sectionId: variables?.sectionId || '',
      eventId: variables?.eventId || eventId,
    },
    songsCount: section?.songsCount,
  });
  const {
    songsList,
    sectionSongsData,
    loadMoreSongs,
    refetchSongs,
    songsFilter,
    songsLoading,
    isFilterApplied,
    clearFiltering,
    clearSongs,
  } = songsOptions;

  const isShouldBlockShown = () =>
    !!songsFilter.q ||
    (songsList?.length < INITIAL_SONGS_LIMIT && songsList.length > 0) ||
    !songsList?.length;

  const fullBlockDisplay = useMemo(
    () =>
      isTemplate || isFavoriteSections || songsLoading || isLoadingGlobal
        ? false
        : isShouldBlockShown(),
    [songsList?.length, isLoadingGlobal, songsLoading, isFavoriteSections]
  );

  const isFiltersApplied = Object.keys(songsFilter).length !== 0;
  const prevIsFiltersApplied = usePrevious(isFiltersApplied);

  const handleCollapse = () => {
    clearFiltering();
  };

  const loadSongs = () => {
    if (songsList.length < sectionSongsData.totalCount) {
      loadMoreSongs();
    }
  };

  const loadDifference = sectionSongsData.totalCount - songsList.length;
  const loadMoreCount =
    loadDifference >= LOAD_MORE_SONGS_COUNT ? LOAD_MORE_SONGS_COUNT : loadDifference;

  useEffect(() => {
    if (prevSectionId && prevSectionId !== variables.sectionId) {
      clearFiltering();
      clearSongs();
    }
  }, [variables.sectionId, prevSectionId]);

  useEffect(() => {
    if (!controlRef?.isCollapsed) {
      clearFiltering();
    }
  }, [controlRef?.isCollapsed]);

  useEffect(() => {
    if (sectionSongsData.totalCount > 0 && controlRef?.isCollapsed && !prevIsFiltersApplied) {
      controlRef?.setIsCollapsed(false);
    }
  }, [sectionSongsData.totalCount]);

  return (
    <TimelineSongsContext.Provider
      value={{
        songsOptions,
        refetchSongs,
        section,
        sections,
        controlRef,
      }}
    >
      <SelectListProvider initialIds={songsList.map(({ _id }) => _id)}>
        <SectionBlock
          className={classNames('timelineSongsBlock content-fade-in', classes.timelineSongsBlock, {
            [classes.onlyOneSong]: section.songsCount === 1 && !isFilterApplied,
            [classes.shownContent]: songsList.length >= 1 && !controlRef?.isCollapsed,
            blankEmptyState: songsLoading,
            isFilterApplied,
            noSongsFound: songsList.length === 0,
            'ready _1': (!!sectionSongsData && !songsLoading) || !isEventLoading,
          })}
          controlRef={setControlRef}
          onCollapse={handleCollapse}
          type={EventTimelineTabKey.songs}
          section={section}
          renderHeader={({ caretButton }) => <SongsBlockHeader>{caretButton}</SongsBlockHeader>}
          renderContent={() => (
            <>
              {!!songsList.length || isFilterApplied ? (
                <>
                  <WithEventRole
                    roles={[EventUserType.dj, EventUserType.host]}
                    extraCondition={!isTemplate}
                  >
                    <TimelineSongsHeader />
                  </WithEventRole>
                  <SongsList />
                </>
              ) : songsLoading ? (
                <Spinner />
              ) : (
                <NoSongsFound size="default">{t('addYourFirstSong')}</NoSongsFound>
              )}
              {isFiltersApplied && sectionSongsData.totalCount === 0 ? null : (
                <SectionBlockFooter>
                  {!!loadMoreCount ? (
                    <Button onClick={() => loadSongs()} className="centrate" loading={songsLoading}>
                      {t('loadMore', { count: loadMoreCount })}
                    </Button>
                  ) : null}
                </SectionBlockFooter>
              )}
            </>
          )}
          defaultCollapsed={false}
          defaultShown={fullBlockDisplay}
          canCollapse={!isTemplate && !isFavoriteSections}
        />
      </SelectListProvider>
    </TimelineSongsContext.Provider>
  );
};

export default memo(TimelineSongsBlock);
